/* KitchenBuildr cart page v2.
   Quantity changes, remove with undo, discount codes, delivery notes, delivery estimate, recommendations and the
   mobile checkout bar. Uses the Cart Ajax API + Section Rendering API; only the changing regions are re-rendered,
   so express checkout buttons (Shop Pay, PayPal...) stay intact. */
(function () {
  if (customElements.get('kb-cart')) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var GENERIC_ERROR = 'Something went wrong. Please try again.';

  // England & Wales bank holidays (no dispatch or delivery). Add the next year's dates each autumn.
  var BANK_HOLIDAYS = [
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28'
  ];
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---------------- Delivery estimate helpers (UK time) ---------------- */

  function londonNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date());
      var o = {};
      parts.forEach(function (p) { o[p.type] = p.value; });
      return { y: +o.year, m: +o.month, d: +o.day, secs: (+o.hour % 24) * 3600 + (+o.minute) * 60 + (+o.second) };
    } catch (e) {
      var n = new Date();
      return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate(), secs: n.getHours() * 3600 + n.getMinutes() * 60 + n.getSeconds() };
    }
  }
  function isWorkingDay(date) {
    var dow = date.getUTCDay();
    if (dow === 0 || dow === 6) return false;
    return BANK_HOLIDAYS.indexOf(date.toISOString().slice(0, 10)) === -1;
  }
  function addWorkingDays(date, n) {
    var d = new Date(date.getTime());
    while (n > 0) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (isWorkingDay(d)) n--;
    }
    return d;
  }
  function nextWorkingDay(date) {
    var d = new Date(date.getTime());
    do { d.setUTCDate(d.getUTCDate() + 1); } while (!isWorkingDay(d));
    return d;
  }
  function fmtDay(d) { return DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()]; }
  function fmtRange(a, b) {
    if (a.getTime() === b.getTime()) return fmtDay(a);
    if (a.getUTCMonth() === b.getUTCMonth()) return DAYS[a.getUTCDay()] + ' ' + a.getUTCDate() + ' – ' + fmtDay(b);
    return fmtDay(a) + ' – ' + fmtDay(b);
  }
  function fmtCountdown(secs) {
    var h = Math.floor(secs / 3600);
    var m = Math.max(1, Math.ceil((secs % 3600) / 60));
    if (m === 60) { h += 1; m = 0; }
    return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
  }

  class KBCart extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.sectionId = this.dataset.sectionId;
      this.queue = Promise.resolve();
      this.stepTimers = {};
      this.noteTimer = null;
      this.toastTimer = null;
      this.lastRemoved = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onExternalAdd = this.onExternalAdd.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('kb:cart:added', this.onExternalAdd);

      this.initSticky();
      this.initRecs();
      this.initEta();
      this.initFooterGap();
    }

    disconnectedCallback() {
      document.removeEventListener('kb:cart:added', this.onExternalAdd);
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this.recsCleanup) this.recsCleanup();
      if (this.etaTimer) clearInterval(this.etaTimer);
      if (this.onGapResize) window.removeEventListener('resize', this.onGapResize);
    }

    /* ---------------- Footer gap ----------------
       The footer's "Speak with a kitchen expert" card overlaps the section above it, by a different amount at
       each screen size. Measure how far anything after the cart reaches up into it and pad the cart so the
       recommendations (or the last block) always end a clear gap above that card. */

    initFooterGap() {
      var self = this;
      var timer = null;
      var run = function () { self.fitFooterGap(); };
      this.onGapResize = function () { clearTimeout(timer); timer = setTimeout(run, 150); };
      window.addEventListener('resize', this.onGapResize);
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
      setTimeout(run, 1500);
      setTimeout(run, 4000);
    }

    followingTop() {
      // Top edge of the highest visible element painted after the cart section.
      var start = this.closest('.shopify-section') || this;
      var roots = [];
      var node = start;
      while (node && node !== document.body && roots.length < 8) {
        var sib = node.nextElementSibling;
        while (sib && roots.length < 8) { roots.push(sib); sib = sib.nextElementSibling; }
        node = node.parentElement;
      }
      var cartTop = this.getBoundingClientRect().top;
      var vw = window.innerWidth;
      var top = Infinity;
      var checked = 0;
      for (var i = 0; i < roots.length && checked < 1200; i++) {
        var els = [roots[i]].concat(qsa('*', roots[i]));
        for (var j = 0; j < els.length && checked < 1200; j++) {
          var el = els[j];
          checked++;
          if (/^(SCRIPT|STYLE|LINK|TEMPLATE|META|NOSCRIPT)$/.test(el.tagName)) continue;
          var r = el.getBoundingClientRect();
          if (r.width < 40 || r.height < 20 || r.right <= 0 || r.left >= vw || r.top <= cartTop) continue;
          var cs = window.getComputedStyle(el);
          if (cs.position === 'fixed' || cs.position === 'sticky' || cs.visibility === 'hidden' || cs.opacity === '0') continue;
          if (r.top < top) top = r.top;
        }
      }
      return top;
    }

    fitFooterGap() {
      if (!this.isConnected || !this.offsetHeight) return;
      var gap = window.innerWidth < 750 ? 32 : 56;
      var top = this.followingTop();
      if (!isFinite(top)) return;
      var current = parseFloat(window.getComputedStyle(this).paddingBottom) || 0;
      // The cart's own content ends at (bottom - padding). Whatever follows moves with the padding,
      // so the padding that leaves exactly `gap` above it is current + (gap - visible gap).
      var contentEnd = this.getBoundingClientRect().bottom - current;
      var visibleGap = top - contentEnd;
      if (Math.abs(visibleGap - gap) < 2) return;
      var next = Math.round(Math.min(640, Math.max(0, current + gap - visibleGap)));
      this.style.setProperty('--kbc-measured', next + 'px');
      this.classList.add('is-measured');
    }

    /* ---------------- Requests ---------------- */

    enqueue(task) {
      var run = function () { return task(); };
      this.queue = this.queue.then(run, run);
      return this.queue;
    }

    post(path, payload, withSections) {
      if (withSections !== false) {
        payload.sections = [this.sectionId, 'cart-icon-bubble'];
        payload.sections_url = window.location.pathname;
      }
      return fetch(ROOT + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok && !data.status, data: data };
        });
      });
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      qsa('[data-kb-checkout]', this).forEach(function (btn) { btn.disabled = busy; });
    }

    /* ---------------- Rendering ---------------- */

    render(data) {
      var sections = (data && data.sections) || {};
      var html = sections[this.sectionId];
      var count = typeof data.item_count === 'number' ? data.item_count : null;
      var wasEmpty = this.dataset.itemCount === '0';

      if (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newMain = qs('[data-kb-cart-main]', doc);
        var newCount = qs('kb-cart', doc) ? qs('kb-cart', doc).dataset.itemCount : null;
        if (newCount !== null) count = Number(newCount);

        if (wasEmpty && count > 0) {
          // Express checkout buttons need a fresh page load to initialise.
          window.location.reload();
          return;
        }

        if (count === 0 && newMain) {
          qs('[data-kb-cart-main]', this).innerHTML = newMain.innerHTML;
          this.classList.add('is-empty');
          var recs = qs('[data-kb-cart-recs]', this);
          if (recs) recs.classList.add('is-hidden');
          this.stickyVisible = false;
        } else {
          var state = this.captureState();
          qsa('[data-kb-region]', this).forEach(function (region) {
            var next = qs('[data-kb-region="' + region.dataset.kbRegion + '"]', doc);
            if (next) region.innerHTML = next.innerHTML;
          });
          this.restoreState(state);
        }
      }

      if (count !== null) {
        this.dataset.itemCount = count;
        qsa('[data-kb-cart-count]').forEach(function (el) { el.textContent = count; });
      }

      var bubbleHtml = sections['cart-icon-bubble'];
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        var inner = qs('.shopify-section', bubbleDoc);
        if (inner) bubble.innerHTML = inner.innerHTML;
      }

      try {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'kb-cart', cartData: data });
        }
      } catch (e) {}

      this.updateEta();
      this.updateSticky();
    }

    // Re-render from the server (after an add from elsewhere on the page)
    refresh() {
      var self = this;
      return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(this.sectionId))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var sections = {};
          sections[self.sectionId] = html;
          self.render({ sections: sections });
        })
        .catch(function () {});
    }

    captureState() {
      var active = document.activeElement;
      var state = { note: null, open: [], discount: '' };
      var note = qs('[data-kb-note]', this);
      if (note) state.note = note.value;
      var discountInput = qs('[data-kb-discount-input]', this);
      if (discountInput) state.discount = discountInput.value;
      // Keep opened panels (discount code, notes) open after a re-render.
      qsa('details[id]', this).forEach(function (d) { if (d.open) state.open.push(d.id); });
      if (active && this.contains(active)) {
        var line = active.closest('[data-kb-line]');
        state.key = line ? line.dataset.key : null;
        state.selector = active.matches('[data-kb-qty]') ? '[data-kb-qty]'
          : active.matches('[data-kb-qty-step="1"]') ? '[data-kb-qty-step="1"]'
          : active.matches('[data-kb-qty-step="-1"]') ? '[data-kb-qty-step="-1"]'
          : active.matches('[data-kb-discount-input]') ? '[data-kb-discount-input]' : null;
      }
      return state;
    }

    restoreState(state) {
      var self = this;
      var note = qs('[data-kb-note]', this);
      if (note && state.note !== null && note.value !== state.note) note.value = state.note;
      state.open.forEach(function (id) {
        var d = qs('#' + id, self);
        if (d) d.open = true;
      });
      if (!state.selector) return;
      var scope = state.key ? this.lineByKey(state.key) : this;
      var target = scope && qs(state.selector, scope);
      if (target && !target.disabled) target.focus({ preventScroll: true });
    }

    lineByKey(key) {
      return qsa('[data-kb-line]', this).filter(function (l) { return l.dataset.key === key; })[0] || null;
    }

    announce(message) {
      var live = qs('[data-kb-live]', this);
      if (!live) return;
      live.textContent = '';
      setTimeout(function () { live.textContent = message; }, 50);
    }

    /* ---------------- Delivery estimate ---------------- */

    initEta() {
      var self = this;
      this.updateEta();
      this.etaTimer = setInterval(function () { if (!document.hidden) self.updateEta(); }, 30000);
    }

    updateEta() {
      var targets = qsa('[data-kb-eta]', this);
      if (!targets.length) return;

      var dispatchDays = Math.max(1, Number(this.dataset.dispatchDays) || 3);
      var cutoff = Number(this.dataset.cutoffHour);
      if (!isFinite(cutoff)) cutoff = 14;
      var tMin = Math.max(0, Number(this.dataset.transitMin) || 2);
      var tMax = Math.max(tMin, Number(this.dataset.transitMax) || tMin);

      var now = londonNow();
      var today = new Date(Date.UTC(now.y, now.m - 1, now.d));
      var left = cutoff * 3600 - now.secs;
      var beforeCutoff = isWorkingDay(today) && left > 0;
      var start = beforeCutoff ? today : nextWorkingDay(today);
      var dispatchBy = addWorkingDays(start, dispatchDays);
      var range = fmtRange(addWorkingDays(dispatchBy, tMin), addWorkingDays(dispatchBy, tMax));
      var clock = '<svg class="kbc-i" aria-hidden="true"><use href="#kbc-i-clock"/></svg>';

      targets.forEach(function (el) {
        var html;
        if (el.dataset.kbEta === 'short') {
          html = clock + '<span>Estimated delivery <b>' + range + '</b></span>';
        } else if (beforeCutoff && left <= 12 * 3600) {
          html = clock + '<span>Order within <b>' + fmtCountdown(left) + '</b> for estimated delivery <b>' + range + '</b></span>';
        } else {
          html = clock + '<span>Estimated delivery <b>' + range + '</b> · dispatched within ' + dispatchDays + ' working days</span>';
        }
        if (el.innerHTML !== html) el.innerHTML = html;
        el.hidden = false;
      });
    }

    /* ---------------- Line items ---------------- */

    showLineError(line, message) {
      var el = line && qs('[data-kb-line-error]', line);
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    changeLine(line, quantity, removal) {
      var self = this;
      var key = line.dataset.key;
      var title = line.dataset.title || 'Item';
      var previous = Number(line.dataset.quantity);
      if (quantity === previous) return Promise.resolve();

      line.classList.add('is-loading');
      this.showLineError(line, '');
      this.setBusy(true);

      return this.enqueue(function () {
        return self.post('cart/change.js', { id: key, quantity: quantity }).then(function (res) {
          if (!res.ok) {
            var input = qs('[data-kb-qty]', line);
            if (input) input.value = previous;
            line.classList.remove('is-loading');
            self.showLineError(line, res.data.description || res.data.message || GENERIC_ERROR);
            self.announce(res.data.description || GENERIC_ERROR);
            return;
          }

          var updated = (res.data.items || []).filter(function (i) { return i.key === key; })[0];
          self.render(res.data);

          if (quantity === 0) {
            self.announce(title + ' removed from your cart');
            if (removal) self.showUndo(removal);
          } else if (updated && updated.quantity < quantity) {
            var fresh = self.lineByKey(key);
            var msg = 'Only ' + updated.quantity + ' available. We have updated your quantity.';
            self.showLineError(fresh, msg);
            self.announce(msg);
          } else {
            self.announce('Cart updated');
          }
        }).catch(function () {
          line.classList.remove('is-loading');
          self.showLineError(line, GENERIC_ERROR);
        });
      }).then(function () { self.setBusy(false); });
    }

    removeLine(line) {
      var removal = {
        id: Number(line.dataset.variantId),
        quantity: Number(line.dataset.quantity),
        title: line.dataset.title || 'Item'
      };
      try {
        var props = JSON.parse(line.dataset.properties || '{}');
        if (props && typeof props === 'object' && Object.keys(props).length) removal.properties = props;
      } catch (e) {}
      if (line.dataset.sellingPlan) removal.selling_plan = Number(line.dataset.sellingPlan);
      return this.changeLine(line, 0, removal);
    }

    showUndo(removal) {
      var toast = qs('[data-kb-toast]', this);
      if (!toast) return;
      this.lastRemoved = removal;
      qs('[data-kb-toast-text]', toast).textContent = '"' + removal.title + '" removed.';
      toast.hidden = false;
      requestAnimationFrame(function () { toast.classList.add('is-visible'); });
      clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(function () { self.hideToast(); }, 7000);
    }

    hideToast() {
      var toast = qs('[data-kb-toast]', this);
      if (!toast) return;
      toast.classList.remove('is-visible');
      setTimeout(function () { toast.hidden = true; }, 200);
    }

    undoRemove() {
      var item = this.lastRemoved;
      if (!item) return;
      this.lastRemoved = null;
      this.hideToast();
      var self = this;
      var line = { id: item.id, quantity: item.quantity };
      if (item.properties) line.properties = item.properties;
      if (item.selling_plan) line.selling_plan = item.selling_plan;
      this.setBusy(true);
      this.enqueue(function () {
        return self.post('cart/add.js', { items: [line] }).then(function (res) {
          if (!res.ok) { self.announce(res.data.description || GENERIC_ERROR); return; }
          self.render(res.data);
          self.announce(item.title + ' added back to your cart');
          if (window.KBProductCard && window.KBProductCard.refreshCartCount) window.KBProductCard.refreshCartCount();
        }).catch(function () { self.announce(GENERIC_ERROR); });
      }).then(function () { self.setBusy(false); });
    }

    stepQuantity(button) {
      var line = button.closest('[data-kb-line]');
      var input = line && qs('[data-kb-qty]', line);
      if (!input || input.disabled) return;
      var step = Number(input.step) || 1;
      var min = Number(input.dataset.min) || 1;
      var max = input.max ? Number(input.max) : Infinity;
      var value = Number(input.value) || 0;
      var next = value + Number(button.dataset.kbQtyStep) * step;
      if (next > 0 && next < min) next = Number(button.dataset.kbQtyStep) < 0 ? 0 : min;
      next = Math.max(0, Math.min(max, next));
      input.value = next;

      var key = line.dataset.key;
      var self = this;
      clearTimeout(this.stepTimers[key]);
      // Batch fast clicks into one request
      this.stepTimers[key] = setTimeout(function () {
        var current = self.lineByKey(key) || line;
        if (next === 0) self.removeLine(current);
        else self.changeLine(current, next);
      }, 350);
    }

    /* ---------------- Discount codes ---------------- */

    discountCodes() {
      var state = qs('[data-kb-discount-state]', this);
      try {
        var codes = JSON.parse((state && state.dataset.codes) || '[]');
        return Array.isArray(codes) ? codes : [];
      } catch (e) { return []; }
    }

    discountMessage(message, type) {
      var el = qs('[data-kb-discount-msg]', this);
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
      el.className = 'kbc-discount__msg' + (type ? ' is-' + type : '');
    }

    applyDiscount() {
      var input = qs('[data-kb-discount-input]', this);
      if (!input) return;
      var code = input.value.trim();
      if (!code) { this.discountMessage('Enter a discount code.', 'error'); input.focus(); return; }
      var existing = this.discountCodes();
      if (existing.some(function (c) { return c.toLowerCase() === code.toLowerCase(); })) {
        this.discountMessage('That code is already applied.', 'error');
        return;
      }

      var self = this;
      var fallback = ROOT + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
      var button = qs('[data-kb-discount-apply]', this);
      if (button) button.classList.add('is-loading');
      this.setBusy(true);

      this.enqueue(function () {
        return self.post('cart/update.js', { discount: existing.concat(code).join(',') }).then(function (res) {
          if (!res.ok) throw new Error('update failed');
          self.render(res.data);
          var fresh = qs('[data-kb-discount-input]', self);
          if (fresh) fresh.value = '';
          var match = qsa('[data-kb-code]', self).filter(function (el) {
            return el.dataset.kbCode.toLowerCase() === code.toLowerCase();
          })[0];
          if (!match) {
            // Older stores without Ajax discount support: use the discount link instead.
            window.location.href = fallback;
            return;
          }
          if (match.dataset.applicable === 'false') {
            self.discountMessage('"' + code + '" can\'t be used with the items in your cart.', 'error');
            if (fresh) fresh.value = code;
          } else {
            self.discountMessage('Discount applied.', 'success');
            self.announce('Discount ' + code + ' applied');
          }
        }).catch(function () { window.location.href = fallback; });
      }).then(function () {
        self.setBusy(false);
        var b = qs('[data-kb-discount-apply]', self);
        if (b) b.classList.remove('is-loading');
      });
    }

    removeDiscount(code) {
      var self = this;
      var remaining = this.discountCodes().filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
      this.setBusy(true);
      this.enqueue(function () {
        return self.post('cart/update.js', { discount: remaining.join(',') }).then(function (res) {
          if (!res.ok) throw new Error('update failed');
          self.render(res.data);
          self.announce('Discount ' + code + ' removed');
        }).catch(function () { self.discountMessage(GENERIC_ERROR, 'error'); });
      }).then(function () { self.setBusy(false); });
    }

    /* ---------------- Note ---------------- */

    saveNote(textarea) {
      var self = this;
      var status = qs('[data-kb-note-status]', this);
      clearTimeout(this.noteTimer);
      if (status) status.textContent = '';
      this.noteTimer = setTimeout(function () {
        self.post('cart/update.js', { note: textarea.value }, false)
          .then(function (res) { if (status) status.textContent = res.ok ? 'Saved' : GENERIC_ERROR; })
          .catch(function () { if (status) status.textContent = GENERIC_ERROR; });
      }, 600);
    }

    /* ---------------- Events ---------------- */

    onClick(event) {
      var t = event.target;
      var step = t.closest('[data-kb-qty-step]');
      if (step) { event.preventDefault(); this.stepQuantity(step); return; }

      var remove = t.closest('[data-kb-remove]');
      if (remove) {
        event.preventDefault();
        var line = remove.closest('[data-kb-line]');
        if (line && !line.classList.contains('is-loading')) this.removeLine(line);
        return;
      }

      if (t.closest('[data-kb-discount-apply]')) { event.preventDefault(); this.applyDiscount(); return; }

      var removeCode = t.closest('[data-kb-discount-remove]');
      if (removeCode) { event.preventDefault(); this.removeDiscount(removeCode.dataset.kbDiscountRemove); return; }

      if (t.closest('[data-kb-undo]')) { event.preventDefault(); this.undoRemove(); }
    }

    onChange(event) {
      var input = event.target;
      if (!input.matches('[data-kb-qty]')) return;
      var line = input.closest('[data-kb-line]');
      if (!line) return;
      var value = Math.floor(Number(input.value));
      if (!isFinite(value) || value < 0) value = Number(line.dataset.quantity);
      if (input.max && value > Number(input.max)) value = Number(input.max);
      input.value = value;
      clearTimeout(this.stepTimers[line.dataset.key]);
      if (value === 0) this.removeLine(line);
      else this.changeLine(line, value);
    }

    onInput(event) {
      if (event.target.matches('[data-kb-note]')) this.saveNote(event.target);
      if (event.target.matches('[data-kb-discount-input]')) this.discountMessage('');
    }

    onKeydown(event) {
      if (event.key !== 'Enter') return;
      var t = event.target;
      // Enter must never submit the checkout form from these fields.
      if (t.matches('[data-kb-discount-input]')) { event.preventDefault(); this.applyDiscount(); }
      else if (t.matches('[data-kb-qty]')) { event.preventDefault(); t.blur(); }
    }

    onExternalAdd() {
      // A recommendation card added something: refresh lines and totals.
      this.refresh();
    }

    /* ---------------- Mobile sticky checkout ---------------- */

    initSticky() {
      var self = this;
      this.sticky = qs('[data-kb-sticky]', this);
      var main = qs('[data-kb-checkout-main]', this);
      if (!this.sticky || !main || !window.IntersectionObserver) return;
      this.stickyObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        // Show only while the real checkout button is still below the viewport.
        self.stickyVisible = !entry.isIntersecting && entry.boundingClientRect.top > 0;
        self.updateSticky();
      });
      this.stickyObserver.observe(main);
    }

    updateSticky() {
      if (!this.sticky) return;
      var show = !!this.stickyVisible && this.dataset.itemCount !== '0';
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      var btn = qs('button', this.sticky);
      if (btn) btn.tabIndex = show ? 0 : -1;
    }

    /* ---------------- Recommendations ---------------- */

    initRecs() {
      var section = qs('[data-kb-cart-recs]', this);
      if (!section) return;
      var track = qs('[data-kb-recs-track]', section);
      var self = this;

      if (section.dataset.url && window.fetch) {
        fetch(section.dataset.url)
          .then(function (r) { if (!r.ok) throw new Error(); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var results = qs('[data-kb-cart-rec-results]', doc);
            if (results && qs('.kb-card', results)) {
              track.innerHTML = results.innerHTML;
              section.classList.remove('is-hidden');
            } else if (!qs('.kb-card', track)) {
              section.classList.add('is-hidden');
            }
            self.updateRecArrows();
            self.fitFooterGap();
          })
          .catch(function () { if (!qs('.kb-card', track)) section.classList.add('is-hidden'); });
      }

      this.recTrack = track;
      var prev = qs('[data-kb-recs-prev]', section);
      var next = qs('[data-kb-recs-next]', section);
      var stopped = false;
      var timer = null;

      function stepSize() {
        var cards = qsa('.kb-card', track);
        if (cards.length < 2) return track.clientWidth;
        return cards[1].offsetLeft - cards[0].offsetLeft || track.clientWidth;
      }
      function maxScroll() { return Math.max(0, track.scrollWidth - track.clientWidth); }
      function go(dir) {
        var max = maxScroll();
        var target = track.scrollLeft + dir * stepSize();
        if (dir > 0 && track.scrollLeft >= max - 2) target = 0;
        else if (dir < 0 && track.scrollLeft <= 2) target = max;
        else target = Math.max(0, Math.min(max, target));
        track.scrollTo({ left: target, behavior: REDUCE_MOTION ? 'auto' : 'smooth' });
      }
      function stop() { stopped = true; if (timer) { clearInterval(timer); timer = null; } }

      if (prev) prev.addEventListener('click', function () { stop(); go(-1); });
      if (next) next.addEventListener('click', function () { stop(); go(1); });
      track.addEventListener('pointerdown', stop, { passive: true });
      track.addEventListener('scroll', function () { self.updateRecArrows(); }, { passive: true });

      if (section.dataset.autoplay === 'true' && !REDUCE_MOTION) {
        var interval = Math.max(2, Number(section.dataset.interval) || 4) * 1000;
        timer = setInterval(function () {
          if (stopped || document.hidden || maxScroll() <= 2 || !window.matchMedia('(max-width: 749px)').matches) return;
          go(1);
        }, interval);
      }

      var onResize = function () { self.updateRecArrows(); };
      window.addEventListener('resize', onResize);
      this.recsCleanup = function () { stop(); window.removeEventListener('resize', onResize); };
      this.updateRecArrows();
    }

    updateRecArrows() {
      var section = qs('[data-kb-cart-recs]', this);
      if (!section || !this.recTrack) return;
      section.classList.toggle('is-scrollable', this.recTrack.scrollWidth - this.recTrack.clientWidth > 2);
    }
  }

  customElements.define('kb-cart', KBCart);
})();